import React from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import DownShift from 'downshift';
import {
  Container,
  Input,
  OptionsList,
  Item,
  MenuButton,
  DropDownButton,
  OptionListText,
} from './styled';
import PropTypes from 'prop-types';

defineMessages({
  noResults: {
    id: 'Shared.DropDown.SingleDropDown.idleText',
    defaultMessage: 'No Results',
    description: 'no results message',
  },
});

const itemToString = (item = '') => {
  if (!item) return '';
  if (typeof item === 'string') return item;

  return item.label;
};

const matchInputValue =
  (inputValue, matchHead = false) =>
  (item) => {
    if (!inputValue) return true;
    const itemString = itemToString(item).toLowerCase();
    const inputString = inputValue.toLowerCase();

    const matchIndex = itemString.indexOf(inputString);

    if (matchHead) return matchIndex === 0;

    return matchIndex > -1;
  };

const isHighlighted = (highlightedIndex, index) => {
  return highlightedIndex === index;
};

const isSelected = (selectedItem, item) => {
  return itemToString(selectedItem, 'value') === itemToString(item, 'value');
};

const SingleDropDown = ({
  data = [],
  placeholder,
  customItem,
  customPlaceholder,
  showToolTip = false,
  matchHead = false,
  loading,
  idleText,
  filterOptions = true,
  intl,
  showClear = true,
  disabled = false,
  ...restProps
}) => {
  const { formatMessage } = intl;
  const loadingMsg = formatMessage({
    id: 'Common.loading',
  });
  const noResultsMsg = formatMessage({
    id: 'Shared.DropDown.SingleDropDown.idleText',
  });

  return (
    <DownShift itemToString={itemToString} {...restProps}>
      {({
        getInputProps,
        getItemProps,
        getToggleButtonProps,
        isOpen,
        inputValue,
        selectedItem,
        highlightedIndex,
        clearSelection,
        toggleMenu,
      }) => {
        const noResults = !loading && data.length === 0 && !!inputValue;
        const showIdleText =
          !loading && data.length === 0 && !inputValue && idleText;
        return (
          <div style={{ position: 'relative' }}>
            <Container isOpen={isOpen} showToolTip={showToolTip}>
              {customPlaceholder ? (
                customPlaceholder(getToggleButtonProps)
              ) : (
                <Input
                  className="no-margin"
                  {...getInputProps({ placeholder })}
                  onClick={toggleMenu}
                  disabled={disabled}
                />
              )}

              {selectedItem && showClear ? (
                <DropDownButton
                  onClick={clearSelection}
                  className="mdi mdi-close mdi-18px"
                />
              ) : null}

              <MenuButton
                {...getToggleButtonProps()}
                isOpen={isOpen}
                className="mdi mdi-chevron-down mdi-24px"
              />
            </Container>

            {isOpen ? (
              <OptionsList>
                {loading ? <OptionListText>{loadingMsg}</OptionListText> : null}

                {noResults ? (
                  <OptionListText>{noResultsMsg}</OptionListText>
                ) : null}

                {showIdleText ? (
                  <OptionListText>{idleText}</OptionListText>
                ) : null}

                {(filterOptions
                  ? data.filter(matchInputValue(inputValue, matchHead))
                  : data
                ).map((item, index) => {
                  const itemIsSelected = isSelected(selectedItem, item);
                  const itemIsHighlighted = isHighlighted(
                    highlightedIndex,
                    index,
                  );

                  return (
                    <Item
                      {...getItemProps({ item })}
                      key={`${item.value}-${item.label}`}
                      isHighlighted={itemIsHighlighted}
                      isSelected={customItem ? false : itemIsSelected}
                    >
                      {customItem
                        ? customItem({
                            item,
                            index,
                            data,
                            isSelected: itemIsSelected,
                            isHighlighted: itemIsHighlighted,
                          })
                        : itemToString(item)}
                    </Item>
                  );
                })}
              </OptionsList>
            ) : null}
          </div>
        );
      }}
    </DownShift>
  );
};

SingleDropDown.propTypes = {
  data: PropTypes.object,
  placeholder: PropTypes.object,
  customItem: PropTypes.object,
  customPlaceholder: PropTypes.object,
  showToolTip: PropTypes.bool,
  matchHead: PropTypes.bool,
  loading: PropTypes.bool,
  idleText: PropTypes.string,
  filterOptions: PropTypes.object,
  intl: PropTypes.object,
  showClear: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default injectIntl(SingleDropDown);
